import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import { CiFilter } from "react-icons/ci";
import DispatchForm from "./dispatchEntryForm";
import AddDispatchEntryModal from "./addDispatchEntryModal";
import PaginationControls from "../../../setup/components/Building/components/Renders/PaginationControls";
import SearchIcon from "@mui/icons-material/Search";
import API_BASE_URL from "../../../../Constants";

const DispatchList = ({ user }) => {
    const [dispatches, setDispatches] = useState([]);
    const [viewMode, setViewMode] = useState("table");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isViewOpen, setIsViewOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedDispatchID, setSelectedDispatchID] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [pendingRequest, setPendingRequest] = useState(null);
    const [lockOwner, setLockOwner] = useState('');
    const navigate = useNavigate();

    const DispatchesListFields = [
        { key: "actions", label: "Actions" },
        { key: "dispatchNumber", label: "Dispatch Number" },
        { key: "buildingSite", label: "Building Site" },
        { key: "deviceID", label: "Device ID" },
        { key: "billingStatus", label: "Billing Status" },
        { key: "ticketType", label: "Ticket Type" },
        { key: "ticketNumber", label: "Ticket Number" },
        { key: "customerWQ", label: "Customer WQ" },
        { key: "deviceStatus", label: "Device Status" },
        { key: "problemClass", label: "Problem Class" },
        { key: "subClass", label: "Sub Class" },
        { key: "startDate", label: "Start Date" },
        { key: "maintenancePerformed", label: "Maintenance Performed", type: "checkbox" },
        { key: "inspectionPerformed", label: "Inspection Performed", type: "checkbox" },
        { key: "allItemsCompleted", label: "All Items Completed", type: "checkbox" },
    ];

    const apiCall = async (url, options = {}) => {
        try {
            const response = await fetch(url, options);
            if (!response.ok) throw new Error(response.statusText);
            const contentType = response.headers.get('Content-Type');
            return contentType && contentType.includes('application/json') ? response.json() : response.text();
        } catch (error) {
            console.error('API call failed:', error);
            throw error;
        }
    };

    const fetchDispatches = useCallback(
        async (page = currentPage, size = rowsPerPage, query = searchQuery) => {
            try {
                const queryString = query ? `&q=${encodeURIComponent(query)}` : "";
                const response = await fetch(
                    `${API_BASE_URL}/api/callcenter/dispatchentry/dispatches?page=${page - 1}&size=${size}${queryString}`,
                    { credentials: "include" }
                );
                if (!response.ok) throw new Error("Network response was not ok");
                const data = await response.json();
                setDispatches(Array.isArray(data.content) ? data.content : []);
                setCurrentPage(data.number + 1);
                setTotalPages(data.totalPages);
            } catch (error) {
                console.error("Fetch error: ", error);
                setDispatches([]);
            }
        },
        [currentPage, rowsPerPage, searchQuery]
    );

    useEffect(() => {
        fetchDispatches(currentPage, rowsPerPage, searchQuery);
    }, [fetchDispatches, currentPage, rowsPerPage, searchQuery]);

    const handlePageChange = (page) => setCurrentPage(page);
    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
        fetchDispatches(1, rowsPerPage, e.target.value);
    };

    const handleOpenCreateModal = () => setIsCreateOpen(true);
    const handleCloseCreateModal = () => setIsCreateOpen(false);

    const handleOpenViewModal = (id) => {
        const selectedDispatch = dispatches.find((item) => item.dispatchId === id);
        setSelectedDispatchID(selectedDispatch);
        setIsViewOpen(true);
    };
    const handleCloseViewModal = () => setIsViewOpen(false);

    return (
        <div className="mx-auto pl-4 pr-4 h-screen w-full">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">Dispatch List</h2>
                <div className="flex justify-end gap-4">
                    <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                        <div className="px-3 py-2 text-gray-500">
                            <SearchIcon />
                        </div>
                        <input
                            type="text"
                            className="px-3 py-2 w-full outline-none"
                            placeholder="Search"
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />
                    </div>
                    <div>
                        <button className="py-2 px-3 border-sss-blue-sel rounded-lg flex border text-sss-blue-sel font-bold hover:bg-sss-blue hover:text-white transition duration-300 ease-in-out">
                            <CiFilter className="text-2xl" />
                            Filter
                        </button>
                    </div>
                    <div>
                        <button
                            className="border border-sss-accent-yel bg-sss-accent-yel rounded-lg px-5 py-2 flex text-sss-blue-sel font-bold hover:bg-yellow-500 transition duration-300 ease-in-out"
                            onClick={handleOpenCreateModal}
                        >
                            Add +
                        </button>
                    </div>
                    <div>
                        <label className="flex items-center">
                            <span className="mr-2">View Mode:</span>
                            <select
                                value={viewMode}
                                onChange={(e) => setViewMode(e.target.value)}
                                className="px-4 mr-10 py-1 border rounded-md"
                            >
                                <option value="table">Table</option>
                                <option value="card">Card</option>
                            </select>
                        </label>
                    </div>
                </div>
            </div>

            {viewMode === "table" ? (
                <table className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden border-2 border-gray-200">
                    <thead className="bg-gray-200">
                        <tr>
                            {DispatchesListFields.map((field) => (
                                <th key={field.key} className="px-4 py-2 text-left font-bold text-sss-blue-sel">
                                    {field.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="text-gray-700">
                        {dispatches.map((Dispatch) => (
                            <tr key={Dispatch.buildingID} className="border-t">
                                {DispatchesListFields.map((field) => (
                                    <td key={field.key} className="px-4 py-2">
                                        {field.key === "actions" ? (
                                            <button
                                                className="text-sss-info mr-2"
                                                onClick={() => handleOpenViewModal(Dispatch.dispatchId)}
                                            >
                                                View
                                            </button>
                                        ) : field.type === "checkbox" ? (
                                            Dispatch[field.key] ? "True" : "False"
                                        ) : (
                                            Dispatch[field.key]
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {dispatches.map((Dispatch) => (
                        <div
                            key={Dispatch.buildingID}
                            className="bg-white p-4 rounded-lg shadow-lg border-2 border-gray-200 flex items-center"
                        >
                            <img src="/favicon.ico" alt="Profile" className="w-16 h-16 rounded-full mr-4" />
                            <div className="flex-1">
                                {DispatchesListFields.map((field) => (
                                    <div key={field.key} className={field.key === "actions" ? "flex mt-2 space-x-2" : ""}>
                                        {field.key === "actions" ? (
                                            <button
                                                className="text-sss-info mr-2"
                                                onClick={() => handleOpenViewModal(Dispatch.dispatchId)}
                                            >
                                                View
                                            </button>
                                        ) : (
                                            <p className="text-sm text-gray-600">
                                                {field.label}: {field.type === "checkbox" ? (Dispatch[field.key] ? "True" : "False") : Dispatch[field.key]}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <AddDispatchEntryModal
                mode="View"
                formDataProp={selectedDispatchID}
                open={isViewOpen}
                handleClose={handleCloseViewModal}
                component={DispatchForm}
                fetchDispatches={fetchDispatches}
            />

            <AddDispatchEntryModal
                mode="Add"
                open={isCreateOpen}
                handleClose={handleCloseCreateModal}
                component={DispatchForm}
                fetchDispatches={fetchDispatches}
            />

            <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
            />
        </div>
    );
};

export default DispatchList;
